import { place, type RoomContext, type RoomDecorator } from '../index';
import * as kit from '../../castle-kit';
import * as g from '../../castle-kit-grounds';

/**
 * THE KING'S SOLAR (royal-tower) — the lord's private chamber at the top of the keep. A tall, narrow tower
 * room kept deliberately SPARE: regal, warm, intimate. The HERO is the grand canopied FOUR-POSTER BED filling
 * the north end, marked by a royal banner and lit by a single bedside candelabra. Below it, ONE quiet beat:
 * a private fireside reading nook — a carved chair drawn to a small table beside the hearth (the room's only
 * bright pool of light), with the lord's tame raven perched at the corner. Crest tapestries frame the two
 * long walls; a blue-and-gold runner draws the chamber's spine; a touch of living green at the bed's foot.
 *
 * Composition: hero bed (north) → supporting fireside nook (south) → heraldic frame on the walls → open floor
 * down the spine. Decluttered from the original heap: dropped the chapel-flavoured reliquary + votive stand
 * (off-narrative for a bedroom), a duplicate wall torch, the dozing hound and the spare table, collapsing
 * four scattered light sources to two well-placed pools (bed candelabra + hearth). Empty floor is intentional.
 *
 * Geometry: zone (3240,2040) 320×600 → centre (3400,2340). Doors N@x3400 (orchard) + W@y2340 (catacombs)
 * are kept clear — set-dressing hugs the side walls and the bed/nook, leaving the central spine walkable.
 */
const decorate: RoomDecorator = (ctx: RoomContext) => {
  const { art } = ctx;

  // ── Floor: the royal runner down the chamber's spine (laid first, under everything). ──
  place(ctx, kit.runnerRug(art, 160, 90), 3400, 2440);

  // ── HERO: the grand canopied four-poster bed filling the north end, banner above, candelabra at the side. ──
  place(ctx, g.fourPosterBed(art), 3400, 2200);
  place(ctx, kit.banner(art, 0x2f4a8a, 110), 3400, 2110);
  place(ctx, kit.candelabra(art), 3340, 2260);
  place(ctx, kit.pottedLily(art), 3540, 2150); // a touch of living green at the bed's foot

  // ── Heraldic frame: a crest tapestry on each long wall. ──
  place(ctx, kit.tapestry(art, 90, 130, 'crest'), 3260, 2300);
  place(ctx, kit.tapestry(art, 90, 130, 'crest'), 3540, 2300);

  // ── The private fireside NOOK — the room's one supporting beat (south end). ──
  // SEAT — a duck sits HERE on this exact chair (load-bearing: royal-tower-chair @3400,2340).
  place(ctx, kit.woodChair(art, 0), 3400, 2340);
  place(ctx, kit.smallTable(art, 30), 3460, 2400);
  place(ctx, kit.fireplace(art, 80), 3300, 2520); // the room's brightest (and only southern) light pool
  place(ctx, kit.wallTorch(art), 3540, 2480); // one mirrored wall light on the east wall — no scatter
  place(ctx, kit.raven(art), 3500, 2600); // the lord's tame raven perched in the warm south-east corner
};

export default decorate;
